import type { RouterHistory } from "@tanstack/react-router";

import { createQueryClient } from "../api/query-client";
import { forgetMediaAccess } from "../platform/media/access";
import { onPublicRoute } from "../platform/routes";
import { returnPath } from "../platform/session";
import { createAppRouter } from "./router";

/**
 * The query client and the router, wired together. A 401 from any query or mutation after the
 * page loaded clears the cache and the media grants, so nothing of an ended session stays, and
 * goes to sign-in with a way back. During a navigation the route guard and the error screen
 * handle it, and a public route (sign-in, the invitation link) handles its own.
 */
export function createApp(history?: RouterHistory) {
  let router: ReturnType<typeof createAppRouter> | undefined;
  const queryClient = createQueryClient({
    onSessionExpired: () => {
      if (router === undefined) return;
      const { location, status, matches } = router.state;
      if (status === "pending" || onPublicRoute(matches)) return;
      queryClient.clear();
      forgetMediaAccess();
      void router.navigate({
        to: "/sign-in",
        search: { redirect: returnPath(location) },
        replace: true,
      });
    },
  });
  router = createAppRouter({ queryClient, ...(history === undefined ? {} : { history }) });
  return { queryClient, router };
}
